import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Modal, Text, TextButton, PrimaryButton, Autocomplete, Field, Alert, IconButton } from '@artprov/design-system'
import FormDropdown from './FormDropdown'
import { validarCuitDuplicada, normalizarCuit } from '../util/validarCuitDuplicada'
import { getEmpresaPorCuit } from '@/common/utils/services/usuarios.service'
import { TOAST_MESSAGES } from '../constants/formConstants'

const CUIT_REGEX = /^\d{2}-\d{8}-\d$/

// Valida formato, prefijo y dígito verificador (módulo 11), igual que la
// variante 'cuil' del design-system
const PREFIJOS_CUIT_VALIDOS = ['20', '23', '24', '27', '30', '33', '34', '96']

const validarCuit = (cuit) => {
  if (!CUIT_REGEX.test(cuit)) return false
  const [prefijo, cuerpo, verificador] = cuit.split('-')
  if (!PREFIJOS_CUIT_VALIDOS.includes(prefijo)) return false

  const pesos = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]
  const digitos = (prefijo + cuerpo).split('').map(Number)
  const suma = digitos.reduce((acc, digito, i) => acc + digito * pesos[i], 0)
  const resto = suma % 11
  // Resto 0 → verificador 0 y resto 1 → 9 son los casos especiales del módulo 11
  let verificadorEsperado = 11 - resto
  if (resto === 0) verificadorEsperado = 0
  else if (resto === 1) verificadorEsperado = 9

  return Number(verificador) === verificadorEsperado
}

export default function ModalAsociarCuenta({
  open,
  setOpen,
  onCancel,
  onSubmit,
  isSubmitting = false,
  cargoOptions = [],
  areaOptions = [],
  cuentas = [],
}) {
  const [form, setForm] = useState({
    cuit: '',
    cargo: '',
    cargoId: '',
    areaAfinidad: '',
    areaAfinidadId: '',
  })

  const [cargoSearchValue, setCargoSearchValue] = useState('')

  // Estados de validación de CUIT
  const [esDuplicada, setEsDuplicada] = useState(false)
  const [validandoCuit, setValidandoCuit] = useState(false)
  const [cuitValidada, setCuitValidada] = useState(false)
  const [errorCuitServicio, setErrorCuitServicio] = useState(false)
  // Datos reales de la empresa (razón social y contrato) devueltos por Core Company
  const [empresaValidada, setEmpresaValidada] = useState(null)

  // Evita validar la misma CUIT dos veces (ej. re-render con mismo valor)
  const ultimaCuitValidada = useRef('')

  const handleCargoChange = useCallback((opcion) => {
    const id = opcion?.id ?? ''
    const descripcion = opcion?.descripcion ?? ''
    setForm((prev) => ({
      ...prev,
      cargo: descripcion,
      cargoId: id,
    }))
    setCargoSearchValue(descripcion)
  }, [])

  const handleAreaChange = useCallback((option) => {
    setForm((prev) => ({
      ...prev,
      areaAfinidad: option.label,
      areaAfinidadId: option.value,
    }))
  }, [])

  const handleCuitChange = useCallback((value) => {
    setForm((prev) => ({ ...prev, cuit: value }))
  }, [])

  // Validación de CUIT: duplicada (front) + cliente ART (Core Company).
  // Se dispara cuando la CUIT completa el formato XX-XXXXXXXX-X (11 dígitos),
  // mismo criterio que el proyecto de referencia (evita golpear el servicio en cada tipeo).
  useEffect(() => {
    if (!open) return

    const cuitCompleta = CUIT_REGEX.test(form.cuit)
    if (!cuitCompleta) {
      // CUIT incompleta o inválida: resetear estados de validación
      setEsDuplicada(false)
      setCuitValidada(false)
      setErrorCuitServicio(false)
      setEmpresaValidada(null)
      ultimaCuitValidada.current = ''
      return
    }

    const cuitNormalizada = normalizarCuit(form.cuit)
    if (ultimaCuitValidada.current === cuitNormalizada) return
    ultimaCuitValidada.current = cuitNormalizada

    // 1) Validación de duplicada contra las cuentas ya cargadas (solo front)
    const duplicada = validarCuitDuplicada(form.cuit, cuentas)
    setEsDuplicada(duplicada)

    if (duplicada) {
      // Si es duplicada no se consulta Core Company
      setCuitValidada(false)
      setErrorCuitServicio(false)
      setEmpresaValidada(null)
      return
    }

    // 2) Validación de cliente de Provincia ART vía Core Company
    let cancelado = false
    setValidandoCuit(true)
    setErrorCuitServicio(false)
    setCuitValidada(false)

    getEmpresaPorCuit(cuitNormalizada)
      .then((resultado) => {
        if (cancelado) return
        if (resultado.valida) {
          setCuitValidada(true)
          setEmpresaValidada(resultado.empresa ?? null)
        } else {
          setCuitValidada(false)
          setEmpresaValidada(null)
          // NO_EXISTE / SIN_CONTRATO -> hint "no es cliente"; ERROR -> hint genérico
          setErrorCuitServicio(resultado.motivo === 'ERROR')
        }
      })
      .finally(() => {
        if (!cancelado) setValidandoCuit(false)
      })

    return () => {
      cancelado = true
    }
  }, [form.cuit, cuentas, open])

  const handleClose = useCallback(() => {
    setOpen(false)
    setForm({
      cuit: '',
      cargo: '',
      cargoId: '',
      areaAfinidad: '',
      areaAfinidadId: '',
    })
    setCargoSearchValue('')
    setEsDuplicada(false)
    setValidandoCuit(false)
    setCuitValidada(false)
    setErrorCuitServicio(false)
    setEmpresaValidada(null)
    ultimaCuitValidada.current = ''
  }, [setOpen])

  const handleCancel = useCallback(() => {
    handleClose()
    onCancel?.()
  }, [handleClose, onCancel])

  const handleSubmit = useCallback(() => {
    if (isSubmitting || validandoCuit) return
    // El cierre del modal lo maneja el padre tras agregar la solicitud pendiente visual
    onSubmit?.({ ...form, empresa: empresaValidada })
  }, [form, empresaValidada, onSubmit, isSubmitting, validandoCuit])

  // Resetear el formulario cuando el modal se cierra (incluye el cierre
  // automático que hace el padre tras agregar la solicitud pendiente)
  useEffect(() => {
    if (!open) {
      setForm({
        cuit: '',
        cargo: '',
        cargoId: '',
        areaAfinidad: '',
        areaAfinidadId: '',
      })
      setCargoSearchValue('')
      setEsDuplicada(false)
      setValidandoCuit(false)
      setCuitValidada(false)
      setErrorCuitServicio(false)
      setEmpresaValidada(null)
      ultimaCuitValidada.current = ''
    }
  }, [open])

  const isCuitValid = useMemo(() => validarCuit(form.cuit), [form.cuit])

  const cuitHints = useMemo(() => {
    if (!form.cuit || isCuitValid) return []
    return [{ type: 'error', text: 'El CUIT ingresado no es válido' }]
  }, [form.cuit, isCuitValid])

  const isFormValid = isCuitValid && form.cargoId && form.areaAfinidadId && cuitValidada && !esDuplicada

  // Mensaje de error bajo el campo CUIT (prioridad: duplicada > no cliente > error de servicio).
  // Se pasa por la prop `error` del Field: en estado inválido el Field oculta los
  // hints custom y solo renderiza el mensaje de error.
  const cuitErrorMensaje = useMemo(() => {
    if (esDuplicada) return TOAST_MESSAGES.HINT_CUIT_DUPLICADA
    if (isCuitValid && !cuitValidada && !validandoCuit && errorCuitServicio) {
      return TOAST_MESSAGES.HINT_CUIT_ERROR_SERVICIO
    }
    if (isCuitValid && !cuitValidada && !validandoCuit && !errorCuitServicio) {
      return TOAST_MESSAGES.HINT_CUIT_NO_CLIENTE
    }
    return undefined
  }, [esDuplicada, isCuitValid, cuitValidada, validandoCuit, errorCuitServicio])

  return (
    <Modal.Container open={open} setOpen={setOpen}>
      <Modal.Content width="min(95vw, 560px)" height="auto" style={{ maxHeight: '90vh', overflow: 'visible' }}>
        <div className="py-3 px-2 max-w-full max-h-[85vh] overflow-y-auto">
          <div className="flex justify-between items-start mb-4">
            <Text variant="h6" className="text-primary-10 text-lg sm:text-xl">
              Asociar nueva cuenta
            </Text>
            <IconButton icon="close" size="large" className="w-5 h-5" onClick={handleClose} aria-label="Cerrar modal" />
          </div>

          <div className="mb-6">
            <Alert
              variant="warning"
              iconStyle="hidden"
              title=""
              description={
                'La asociación requerirá la aprobación del Administrador.\nTe notificaremos por correo electrónico.'
              }
              descriptionStyle="font-semibold whitespace-pre-line"
              interactive={false}
              buttonText=""
              containerStyle="!max-w-none w-full [&>div:first-child>svg]:hidden"
            />
          </div>

          <div className="mb-4">
            <Field
              variant="cuit"
              label="CUIT"
              placeholder="Ej.: 20-23456789-3"
              value={form.cuit}
              onChange={handleCuitChange}
              startIcon=""
              endIcon=""
              fullWidth
              error={cuitErrorMensaje}
              hints={cuitHints}
            />
          </div>

          <div className="mb-4">
            <Autocomplete
              label="Cargo"
              placeholder="Seleccionar"
              opciones={cargoOptions.map((opt) => ({
                id: opt.value,
                descripcion: opt.label,
              }))}
              searchValue={cargoSearchValue}
              setSearchValue={setCargoSearchValue}
              setSelected={handleCargoChange}
              hints={[]}
              classNameContainer="w-full"
            />
          </div>

          <div className="mb-6">
            <FormDropdown
              label="Área de afinidad"
              placeholder="Seleccionar"
              value={form.areaAfinidad}
              options={areaOptions}
              onSelect={handleAreaChange}
              onBlur={() => {}}
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-4">
            <TextButton
              onClick={handleCancel}
              size="small"
              descriptionTextVariant="subtitle1"
              className="!h-[34px] !min-h-[34px] !px-3 whitespace-nowrap inline-flex items-center justify-center w-full sm:w-auto"
            >
              Cancelar
            </TextButton>

            <PrimaryButton
              onClick={handleSubmit}
              size="small"
              disabled={!isFormValid || isSubmitting || validandoCuit}
              className="!h-[34px] !min-h-[34px] !px-4 whitespace-nowrap inline-flex items-center justify-center w-full sm:w-auto"
            >
              {isSubmitting ? 'Enviando...' : 'Solicitar asociación'}
            </PrimaryButton>
          </div>
        </div>
      </Modal.Content>
    </Modal.Container>
  )
}

ModalAsociarCuenta.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  cargoOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  areaOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  cuentas: PropTypes.arrayOf(
    PropTypes.shape({
      cuit: PropTypes.string,
      estado: PropTypes.string,
    })
  ),
}
